import { Children, ReactNode, useEffect, useRef, useState } from "react";
import {
  App,
  Button,
  DatePicker,
  Descriptions,
  DescriptionsProps,
  Divider,
  Flex,
  Form,
  FormInstance,
  FormItemProps,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  Select,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  SaleItemState,
  SaleOrderAdd,
  SaleOrderAddItem,
  Customer,
} from "@/types/sales";
import dayjs from "dayjs";
import { clampDiscountValue, divided, dp, plus } from "@/utils/calc";
import SText from "@/styles/SText";
import { Product } from "@/types/product";
import { ExtPriceType, SaleOrderType } from "@/pages/Sales/types/orderType";
import SelectOrderType from "../../pages/Order/components/SelectOrderType";
import SelectCustomer from "../../../../components/Selector/SelectCustomer";
import ExtPrice from "../../pages/Order/components/ExtPrice";
import SelectPrice from "../../pages/Order/components/SelectPrice";
import SModalForm from "@/styles/SModalForm";
import useProduct from "./hooks/useProduct";
import useCopy from "@/hooks/useCopy";
import Print from "@/components/Print";
import React from "react";
import { CloseOutlined, ReadOutlined } from "@ant-design/icons";
import { OrderPrintWrap } from "./style";
import { CreditLevelSelect, CurrencySelect, CountrySelect, DepartmentSelect } from "@/components/Selector";
import Dict from "@/components/Dict";
import { DictEnum } from "@/types/dict";
import { dateFormat } from "@/utils/format";
import { extPriceToStrig, formatExtPrice } from "@/utils/formatExtPrice";
import usePage from "@/layout/hooks/usePage";
import { PageId } from "@/types/page";
import { InventoryOrdersItem } from "@/types/purchase";
import { numFormmater } from "@/pages/Requirement/util";

const { Title } = Typography;

interface OrderFormProps extends ModalProps {
  handleCancel: () => void;
  handleOk: (data: SaleOrderAdd, itemData: SaleOrderAddItem, itemId: string) => Promise<void>;
  data: SaleItemState | null;
  leftTitle: ReactNode;
  confirmLoading: boolean;
  readOnly?: boolean;
  disabledItems?: Array<keyof SaleOrderType>;
  miniStyle?: boolean;
}

const OrderForm = ({
  handleOk,
  disabledItems,
  data,
  miniStyle = false,
  ...props
}: OrderFormProps) => {
  const { t } = useTranslation();
  const { onPaste } = useCopy();
  const { notification } = App.useApp()
  const formRef = useRef<FormInstance<SaleOrderType>>(null);
  const printRef = useRef<HTMLDivElement | null>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const [customerId, setCustomerId] = useState<string>('');
  const [detail, setDetail] = useState<SaleOrderType | null>(null);
  const [productKey, setProductKey] = useState<number>(0);
  const [price, setPrice] = useState<string>('');
  const [showList, setShowList] = useState<boolean>(false);
  const [maxDisc, setMaxDisc] = useState<number>(0);
  const [maxBDisc, setMaxBDisc] = useState<number>(0);
  const [pastedCustomer, setPastedCustomer] = useState<Customer | null>(null);
  const [discountedPriceSign, setDiscountedPriceSign] = useState<"+" | "-">("-");
  const [bdiscountedPriceSign, setBdiscountedPriceSign] = useState<"+" | "-">("-");

  const getAdjustmentSignByAmount = (
    amount: string | number | undefined,
    quantity: string | number | undefined,
    itemPrice: string | number | undefined,
    adjustmentPrice: string | number | undefined,
  ): "+" | "-" => {
    const totalAmount = Number(amount || 0);
    const totalQuantity = Number(quantity || 0);
    const priceValue = Number(itemPrice || 0);
    const adjustmentValue = Number(adjustmentPrice || 0);

    if (totalQuantity <= 0 || adjustmentValue <= 0) return "-";

    const minusAmount = totalQuantity * (priceValue - adjustmentValue);
    const plusAmount = totalQuantity * (priceValue + adjustmentValue);

    return Math.abs(totalAmount - plusAmount) < Math.abs(totalAmount - minusAmount) ? "+" : "-";
  };

  const getAdjustedUnitPrice = (itemPrice: number, adjustmentPrice: number, sign: "+" | "-") => {
    return sign === "+" ? itemPrice + adjustmentPrice : itemPrice - adjustmentPrice;
  };


  const { pageId } = usePage();


  useEffect(() => {
    const info = getFormatData(data);
    setDetail(info)
    if (info) {
      formRef?.current?.setFieldsValue(info)
      setCustomerId(info.customerId)
      setProduct(info.product ?? null)
      setPrice(info.itemPriceHis?.price || '')
      setDiscountedPriceSign(getAdjustmentSignByAmount(data?.amount, data?.quantity, data?.price, data?.discountedPrice))
      setBdiscountedPriceSign(getAdjustmentSignByAmount(data?.bamount, data?.bquantity, data?.price, data?.bdiscountedPrice))
    } else {
      setDiscountedPriceSign("-")
      setBdiscountedPriceSign("-")
    }
    return () => {
      setDetail(null)
      setPastedCustomer(null)
    }
  }, [data])

  useEffect(() => {
    formRef.current?.validateFields(["discountedPrice"]).catch(() => undefined);
  }, [discountedPriceSign, price]);

  useEffect(() => {
    formRef.current?.validateFields(["bdiscountedPrice"]).catch(() => undefined);
  }, [bdiscountedPriceSign, price]);

  const productDesc = useProduct({ product, miniStyle })

  const getInput = (props: { maxLength: number; placeholder: string, disabled?: boolean }) => {
    return (
      <Input
        placeholder={props.placeholder}
        maxLength={props.maxLength}
        disabled={props.disabled}
        showCount
      />
    );
  };

  const getPercent = (props: { placeholder: string; disabled: boolean }) => {
    return (
      <InputNumber changeOnWheel
        max={100}
        min={0}
        style={{ width: "100%" }}
        placeholder={props.placeholder}
        formatter={(value) => `${value ? value + "%" : ""}`}
        disabled={props.disabled}
      />
    );
  };

  const getCustomer: (item: SaleOrderType) => DescriptionsProps["items"] = (
    item
  ) => [
      {
        key: "code",
        label: t("sale.code"),
        children: <SText text={item?.order?.code || ""} />,
        span: miniStyle ? 3 : 2,
      },
      {
        key: "shortName",
        label: t("sale.shortName"),
        children: <SText text={item?.order?.shortName || ""} />,
        span: miniStyle ? 3 : 1,
      },
      {
        key: "name1",
        label: t("sale.name1"),
        children: <SText text={item?.order?.name1 || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "name2",
        label: t("sale.name2"),
        children: <SText text={item?.order?.name2 || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "country",
        label: t("sale.country"),
        children: <CountrySelect value={item?.order?.country} readOnly />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "address",
        label: t("sale.address"),
        children: <SText text={item?.order?.address || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "zipCode",
        label: t("sale.zipCode"),
        children: <SText text={item?.order?.zipCode || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "email",
        label: t("sale.email"),
        children: <SText text={item?.order?.email || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "contact",
        label: t("sale.contact"),
        children: <SText text={item?.order?.contact || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "phone",
        label: t("sale.phone"),
        children: <SText text={item?.order?.phone || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "fax",
        label: t("sale.fax"),
        children: <SText text={item?.order?.fax || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "mobile",
        label: t("sale.mobile"),
        children: <SText text={item?.order?.mobile || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "account1",
        label: t("sale.account1"),
        children: <SText text={item?.order?.account1 || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "bank1",
        label: t("sale.bank1"),
        children: <SText text={item?.order?.bank1 || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "swiftCode1",
        label: t("sale.swiftCode1"),
        children: <SText text={item?.order?.swiftCode1 || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "account2",
        label: t("sale.account2"),
        children: <SText text={item?.order?.account2 || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "bank2",
        label: t("sale.bank2"),
        children: <SText text={item?.order?.bank2 || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "swiftCode2",
        label: t("sale.swiftCode2"),
        children: <SText text={item?.order?.swiftCode2 || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "currency",
        label: t("sale.currency"),
        children: <CurrencySelect value={item?.order?.currency} readOnly />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "saleType",
        label: t("sale.saleType"),
        children: (
          <Dict type={DictEnum.SALE_TYPE} value={item?.order?.saleType} readOnly />
        ),
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "creditLevel",
        label: t("sale.creditLevel"),
        children: <CreditLevelSelect value={item?.order?.creditLevel} readOnly />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "creditLine",
        label: t("sale.creditLine"),
        children: <SText text={item?.order?.creditLine || ""} />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "depositRate",
        label: t("sale.depositRate"),
        children: <SText text={item?.order?.depositRate || ""} suffix="%" />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "tradeTerm",
        label: t("sale.tradeTerm"),
        children: (
          <Dict type={DictEnum.SALE_SHIPPING_METHOD} value={item?.order?.tradeTerm} readOnly />
        ),
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "payMethod",
        label: t("sale.payMethod"),
        children: (
          <Dict type={DictEnum.SALE_PAY_METHOD} value={item?.order?.payMethod} readOnly />
        ),
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "taxRate",
        label: t("sale.taxRate"),
        children: <SText text={item?.order?.taxRate || ""} suffix="%" />,
        span: miniStyle ? 3 : undefined,
      },
      {
        key: "org",
        label: t("sale.org"),
        children: <DepartmentSelect readOnly value={item?.order?.orgId} />,
        span: 3,
      },
    ];

  const TitleHeader = (
    <div>
      <span style={{ display: 'inline-block', fontSize: 20, fontWeight: 600, marginTop: 10 }}>{t("sale.orderInfo")}</span>
      {pageId === PageId.SALE_OFFICIAL && <Button type="primary" onClick={() => setShowList(true)} disabled={false} style={{ marginLeft: 10 }}>{t(('purchase.outOrder'))}</Button>}
    </div>
  )

  const saleInputForm: FormItemProps<SaleOrderType>[] = [
    {
      noStyle: true,
      children: TitleHeader
    },
    {
      label: t("sale.orderType"),
      name: "type",
      children: <SelectOrderType readOnly={!!data} />,
      required: true,
      rules: [{ required: true, message: t("sale.plOrderType") }],
    },
    {
      label: t("sale.customerInfo"),
      name: "customerId",
      children: (
        <SelectCustomer
          disabled={!!data}
          defaultList={pastedCustomer ? [pastedCustomer] : []}
          onChangeCustomer={(e) => {
            formRef?.current?.setFieldsValue({
              receiver: e?.shortName || e?.name1 || e?.name2 || e?.code,
              receiverAddress: e?.address || "",
              receiverContact: e?.contact || "",
              receiverMobile: e?.mobile || "",
              country: e?.country,
              taxRate: e?.taxRate,
              itemPriceHis: null,
              customerModel: '',
              mfgr: '',
              currency: e.currency,
              commissionRate: e.commissionRate
            });
            setCustomerId(e.id)
            setProduct(null);
          }}
        />
      ),
      hidden: !!data,
      required: true,
      rules: [{ required: true, message: t("sale.plCustomer") }],
    },
    {
      label: t("sale.innerNumber"),
      name: "innerNumber",
      children: getInput({
        maxLength: 50,
        placeholder: t("sale.plInnerNumber"),
        disabled: disabledItems?.includes('innerNumber')
      }),
    },
    {
      label: t("sale.receivingUnit"),
      name: "receiver",
      children: getInput({
        maxLength: 100,
        placeholder: t("sale.plReceivingUnit"),
        disabled: disabledItems?.includes('receiver')
      }),
    },
    {
      label: t("sale.receivingContact"),
      name: "receiverContact",
      children: getInput({
        maxLength: 50,
        placeholder: t("sale.plReceivingContact"),
        disabled: disabledItems?.includes('receiverContact')
      }),
    },
    {
      label: t("sale.receivingContactMobile"),
      name: "receiverMobile",
      children: getInput({
        maxLength: 50,
        placeholder: t("sale.plReceivingContactMobile"),
        disabled: disabledItems?.includes('receiverMobile')
      }),
    },
    {
      label: t("sale.receivingAddress"),
      name: "receiverAddress",
      children: getInput({
        maxLength: 500,
        placeholder: t("sale.plReceivingAddress"),
        disabled: disabledItems?.includes('receiverAddress')
      }),
    },
    {
      label: t("sale.country"),
      name: "country",
      children: <CountrySelect readOnly={disabledItems?.includes('country')} />,
    },
    // {
    //   label: t("sale.revisePrice"),
    //   name: "extPrice",
    //   valuePropName: "ext",
    //   children: <ExtPrice disabled={disabledItems?.includes('extPrice')} />,
    // },
    {
      label: t("sale.taxRate"),
      name: "taxRate",
      children: getPercent({ placeholder: t("sale.plTaxRate"), disabled: (!!disabledItems?.includes('taxRate') || !!props.readOnly) }),
    },
    {
      label: t("sale.shippingMethod"),
      name: "shippingMethod",
      children: (
        <Dict type={DictEnum.SALE_SHIPPING_METHOD} readOnly={disabledItems?.includes('shippingMethod')} />
      ),
    },
    {
      label: t("sale.currency"),
      name: "currency",
      required: true,
      rules: [{ required: true, message: t("components.plCurrency") }],
      children: (
        <CurrencySelect readOnly={disabledItems?.includes('currency')} />
      ),
    },
    {
      label: t('sale.commissionRate'),
      name: "commissionRate",
      children: (
        <InputNumber changeOnWheel style={{ width: "100%" }} min={0} max={100} suffix="%" disabled={disabledItems?.includes('commissionRate')} />
      ),
    },
  ]
  const handlePrice = (type: 'Q' | 'BQ' | 'AD' | 'BAD' | 'D' | 'BD' | 'AS' | 'BAS', value: string | number) => {
    const currentDiscountedPriceSign = type === 'AS' ? value as "+" | "-" : discountedPriceSign;
    const currentBdiscountedPriceSign = type === 'BAS' ? value as "+" | "-" : bdiscountedPriceSign;
    let PRICE = Number(price)
    let Q = Number(formRef.current?.getFieldValue('quantity') || '') || 0
    let BQ = Number(formRef.current?.getFieldValue('bquantity') || '') || 0
    let AD = Number(formRef.current?.getFieldValue('discountedPrice') || '') || 0
    let BAD = Number(formRef.current?.getFieldValue('bdiscountedPrice') || '') || 0
    let D = Number(formRef.current?.getFieldValue('discount')) || 0
    let BD = Number(formRef.current?.getFieldValue('bdiscount') || '') || 0
    let val = Number(value) || 0
    if (type === 'Q') { //数量
      const unitPrice = getAdjustedUnitPrice(PRICE, AD, currentDiscountedPriceSign);
      formRef.current?.setFieldValue('amount', val * unitPrice || '')
      formRef.current?.setFieldValue('realAmount', val * unitPrice - D || '')
    }
    if (type === 'BQ') { //备品数量
      const unitPrice = getAdjustedUnitPrice(PRICE, BAD, currentBdiscountedPriceSign);
      formRef.current?.setFieldValue('bamount', val * unitPrice || '')
      formRef.current?.setFieldValue('brealAmount', val * unitPrice - BD || '')
    }
    if (type === 'AD' || type === 'AS') { //调整价格
      const adjustment = type === 'AD' ? val : AD;
      const unitPrice = getAdjustedUnitPrice(PRICE, adjustment, currentDiscountedPriceSign);
      formRef.current?.setFieldValue('amount', Q * unitPrice || '')
      formRef.current?.setFieldValue('realAmount', Q * unitPrice - D || '')
    }
    if (type === 'BAD' || type === 'BAS') { //备品调整价格
      const adjustment = type === 'BAD' ? val : BAD;
      const unitPrice = getAdjustedUnitPrice(PRICE, adjustment, currentBdiscountedPriceSign);
      formRef.current?.setFieldValue('bamount', BQ * unitPrice || '')
      formRef.current?.setFieldValue('brealAmount', BQ * unitPrice - BD || '')
    }
    if (type === 'D') { //折扣
      const unitPrice = getAdjustedUnitPrice(PRICE, AD, currentDiscountedPriceSign);
      formRef.current?.setFieldValue('realAmount', Q * unitPrice - val || '')
    }
    if (type === 'BD') { //备品折扣
      const unitPrice = getAdjustedUnitPrice(PRICE, BAD, currentBdiscountedPriceSign);
      formRef.current?.setFieldValue('brealAmount', BQ * unitPrice - val || '')
    }

    let amount = Number(formRef.current?.getFieldValue("amount") || 0)
    let bAmount = Number(formRef.current?.getFieldValue("bamount") || 0)
    const discountValue = Number(formRef.current?.getFieldValue("discount") || 0);
    const nextDiscount = clampDiscountValue(discountValue, amount);
    if (nextDiscount !== discountValue) {
      formRef.current?.setFieldValue("discount", nextDiscount)
      const unitPrice = getAdjustedUnitPrice(PRICE, AD, currentDiscountedPriceSign);
      formRef.current?.setFieldValue('realAmount', Q * unitPrice - nextDiscount || '')
    }
    const bdiscountValue = Number(formRef.current?.getFieldValue("bdiscount") || 0);
    const nextBdiscount = clampDiscountValue(bdiscountValue, bAmount);
    if (nextBdiscount !== bdiscountValue) {
      formRef.current?.setFieldValue("bdiscount", nextBdiscount)
      const unitPrice = getAdjustedUnitPrice(PRICE, BAD, currentBdiscountedPriceSign);
      formRef.current?.setFieldValue('brealAmount', BQ * unitPrice - nextBdiscount || '')

    }

    setMaxDisc(Math.max(amount, 0))
    setMaxBDisc(Math.max(bAmount, 0))

    let RA = Number(formRef.current?.getFieldValue('realAmount')) || 0
    let BRA = Number(formRef.current?.getFieldValue('brealAmount')) || 0
    formRef.current?.setFieldValue('totalRealAmount', RA + BRA)
  }
  const inlineFileds: Array<Array<FormItemProps<SaleOrderType>>> = [
    [
      {
        label: <SText text={t("sale.quantity")} style={{ width: 80 }} />,
        name: "quantity",
        layout: 'vertical',
        children: (
          <InputNumber changeOnWheel
            min={0}
            precision={0}
            placeholder='0'
            disabled={disabledItems?.includes('quantity') || !!!product || !!props.readOnly}
            onChange={val => handlePrice('Q', val + '')}
            style={{ width: 80 }} controls={false}
            onFocus={() => { if (!product) notification.error({ placement: 'top', message: t('product.selProduct') }) }}
          />
        ),
      },
      {
        label: <SText text={t("sale.aPrice")} style={{ width: 80 }} />,
        layout: 'vertical',
        children: <InputNumber changeOnWheel placeholder='0' value={price} disabled style={{ width: 80 }} controls={false} />
      },
      {
        label: <SText text={t("sale.revisePrice")} style={{ width: 60 }} />,
        name: "discountedPrice",
        layout: 'vertical',
        rules: [{
          validator: (_, value) => {
            if (discountedPriceSign === "+" || Number(value || 0) <= Number(price || 0)) return Promise.resolve();
            return Promise.reject(new Error(t("sale.priceMax", { num: price || 0 })));
          }
        }],
        children: (
          <InputNumber changeOnWheel
            min={'0'}
            max={discountedPriceSign === "-" ? price : undefined}
            placeholder='0'
            disabled={disabledItems?.includes('discountedPrice') || !!!product || !!props.readOnly}
            onChange={val => handlePrice('AD', val + '')}
            style={{ width: 60 }} controls={false}
            onFocus={() => { if (!product) notification.error({ placement: 'top', message: t('product.selProduct') }) }}
          />
        ),
      },
      {
        label: <SText text={t("sale.totalPrice")} style={{ width: 100 }} />,
        name: "amount",
        layout: 'vertical',
        children: (
          <InputNumber changeOnWheel
            min={0}
            precision={0}
            placeholder='0'
            disabled style={{ width: 100 }} controls={false} formatter={val => numFormmater(val || 0)}
          />
        ),
      },
      {
        label: <SText text={t("sale.discount")} style={{ width: 100 }} />,
        name: "discount",
        layout: 'vertical',
        children: (
          <InputNumber changeOnWheel
            min={0} max={maxDisc}
            placeholder='0'
            disabled={disabledItems?.includes('discount') || !!!product || !!props.readOnly}
            onChange={val => handlePrice('D', val + '')}
            style={{ width: 100 }} controls={false}
            onFocus={() => { if (!product) notification.error({ placement: 'top', message: t('product.selProduct') }) }}
          />
        ),
      },
      {
        label: <SText text={t("sale.sPrice")} style={{ width: 100 }} />,
        name: "realAmount",
        layout: 'vertical',
        children: (
          <InputNumber changeOnWheel
            min={0}
            precision={0}
            placeholder='0' formatter={val => numFormmater(val || 0)}
            disabled style={{ width: 100 }} controls={false}
          />
        ),
      },
    ],
    [
      {
        label: <SText text={t("sale.send")} style={{ width: 80 }} />,
        name: "bquantity",
        layout: 'vertical',
        children: (
          <InputNumber changeOnWheel
            min={0}
            precision={0}
            placeholder='0'
            disabled={disabledItems?.includes('bquantity') || !!!product || !!props.readOnly}
            onChange={val => handlePrice('BQ', val + '')}
            style={{ width: 80 }} controls={false}
            onFocus={() => { if (!product) notification.error({ placement: 'top', message: t('product.selProduct') }) }}
          />
        ),
      },
      {
        label: <SText text={t("sale.aPrice")} style={{ width: 80 }} />,
        layout: 'vertical',
        children: <InputNumber changeOnWheel placeholder='0' value={price} disabled style={{ width: 80 }} controls={false} />
      },
      {
        label: <SText text={t("sale.revisePrice")} style={{ width: 60 }} />,
        name: "bdiscountedPrice",
        layout: 'vertical',
        rules: [{
          validator: (_, value) => {
            if (bdiscountedPriceSign === "+" || Number(value || 0) <= Number(price || 0)) return Promise.resolve();
            return Promise.reject(new Error(t("sale.priceMax", { num: price || 0 })));
          }
        }],
        children: (
          <InputNumber changeOnWheel
            min={'0'} max={bdiscountedPriceSign === "-" ? price : undefined}
            placeholder='0'
            disabled={disabledItems?.includes('bdiscountedPrice') || !!!product || !!props.readOnly}
            onChange={val => handlePrice('BAD', val + '')}
            style={{ width: 60 }} controls={false}
            onFocus={() => { if (!product) notification.error({ placement: 'top', message: t('product.selProduct') }) }}
          />
        ),
      },
      {
        label: <SText text={t("sale.totalPrice")} style={{ width: 100 }} />,
        name: "bamount",
        layout: 'vertical',
        children: (
          <InputNumber changeOnWheel
            min={0}
            precision={0}
            placeholder='0' formatter={val => numFormmater(val || 0)}
            disabled style={{ width: 100 }} controls={false}
          />
        ),
      },
      {
        label: <SText text={t("sale.discount")} style={{ width: 100 }} />,
        name: "bdiscount",
        layout: 'vertical',
        children: (
          <InputNumber changeOnWheel
            min={0} max={maxBDisc}
            placeholder='0'
            disabled={disabledItems?.includes('bdiscount') || !!!product || !!props.readOnly}
            onChange={val => handlePrice('BD', val + '')}
            style={{ width: 100 }} controls={false}
            onFocus={() => { if (!product) notification.error({ placement: 'top', message: t('product.selProduct') }) }}
          />
        ),
      },
      {
        label: <SText text={t("sale.sendPrice")} style={{ width: 100 }} />,
        name: "brealAmount",
        layout: 'vertical',
        children: (
          <InputNumber changeOnWheel
            min={0}
            precision={0}
            placeholder='0' formatter={val => numFormmater(val || 0)}
            disabled style={{ width: 100 }} controls={false}
          />
        ),
      },
    ]
  ]

  const productInputForm: FormItemProps<SaleOrderType>[] = [
    {
      noStyle: true,
      children: <Title level={5}>{t("sale.orderProduct")}</Title>
    },
    {
      label: t("sale.product"),
      name: "itemPriceHis",
      children: (
        <SelectPrice
          customerId={customerId}
          key={productKey}
          disabled={disabledItems?.includes('itemPriceHis')}
          defaultList={data?.priceHisId && detail?.itemPriceHis ? [detail?.itemPriceHis] : []}
          onChange={(val) => {
            setPrice(val.price)
            setProduct(val?.product ?? null)
            formRef?.current?.setFieldsValue({
              mfgr: val?.mfgr ?? '',
              customerModel: val?.customerModel ?? ''
            });
          }}
        />
      ),
    },
    {
      hidden: !product,
      children: <Descriptions items={productDesc} />
    },
    {
      children: <Flex>
        {inlineFileds[0].map(sitem => {
          if (sitem.name === 'quantity') {
            return <>
              <Form.Item {...sitem} />
              <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>X</div></Flex>
              <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>(</div></Flex>
            </>
          } else if (sitem.name === 'discountedPrice') {
            return <>
              <Flex align="center">
                <Select
                  value={discountedPriceSign}
                  options={[
                    { value: "-", label: "-" },
                    { value: "+", label: "+" },
                  ]}
                  style={{ width: 60, marginTop: 26, marginRight: 10 }}
                  disabled={disabledItems?.includes('discountedPrice') || !!!product || !!props.readOnly}
                  onChange={(val) => {
                    setDiscountedPriceSign(val);
                    handlePrice('AS', val);
                  }}
                />
              </Flex>
              <Form.Item {...sitem} />
              <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>)</div></Flex>
              <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>=</div></Flex>
            </>
          }
          else if (sitem.name === 'discount') {
            return <>
              <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>-</div></Flex>
              <Form.Item {...sitem} />
              <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>=</div></Flex>
            </>
          } else return <Form.Item {...sitem} />
        })}
      </Flex>
    },
    {
      noStyle: true,
      children: <Flex>
        <div style={{ width: 60, marginTop: -24, marginLeft: 640, textAlign: 'center', fontSize: 20 }}>+</div>
      </Flex>
    },
    {
      children: <Flex>
        {inlineFileds[1].map(sitem => {
          if (sitem.name === 'bquantity') {
            return <>
              <Form.Item {...sitem} />
              <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>X</div></Flex>
              <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>(</div></Flex>
            </>
          } else if (sitem.name === 'bdiscountedPrice') {
            return <>
              <Flex align="center">
                <Select
                  value={bdiscountedPriceSign}
                  options={[
                    { value: "-", label: "-" },
                    { value: "+", label: "+" },
                  ]}
                  style={{ width: 60, marginTop: 26, marginRight: 10 }}
                  disabled={disabledItems?.includes('bdiscountedPrice') || !!!product || !!props.readOnly}
                  onChange={(val) => {
                    setBdiscountedPriceSign(val);
                    handlePrice('BAS', val);
                  }}
                />
              </Flex>
              <Form.Item {...sitem} />
              <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>)</div></Flex>
              <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>=</div></Flex>
            </>
          }
          else if (sitem.name === 'bdiscount') {
            return <>
              <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>-</div></Flex>
              <Form.Item {...sitem} />
              <Flex align="center"><div style={{ fontSize: 20, marginTop: 26, marginRight: 10 }}>=</div></Flex>
            </>
          } else return <Form.Item {...sitem} />
        })}
      </Flex>
    },
    {
      noStyle: true,
      children: <Flex>
        <div style={{ width: 60, marginTop: -24, marginLeft: 640, textAlign: 'center', fontSize: 20 }}>=</div>
      </Flex>
    },
    {
      name: "totalRealAmount",
      children: (<Flex>
        <Form.Item label={t("sale.orderTotal")} name='totalRealAmount' style={{ marginLeft: 560, marginBottom: 0, width: 100 }}>
          <InputNumber changeOnWheel
            min={0}
            precision={0}
            placeholder='0' formatter={val => numFormmater(val || 0)}
            disabled style={{ width: 100 }}
          />
        </Form.Item>
      </Flex>
      )
    },
    {
      label: t("sale.mfgr"),
      name: "mfgr",
      children: getInput({ maxLength: 50, placeholder: t("sale.plMfgr"), disabled: disabledItems?.includes('mfgr') }),
    },
    {
      label: t("sale.customerModel"),
      name: "customerModel",
      children: getInput({
        maxLength: 50,
        placeholder: t("sale.plCustomerModel"),
        disabled: disabledItems?.includes('customerModel')
      }),
    },
    {
      label: t("sale.reqDeliDate"),
      name: "reqDeliDate",
      getValueProps: (value) => ({ value: value && dayjs(Number(value)) }),
      normalize: (value) => value && dayjs(value).valueOf(),
      children: <DatePicker style={{ width: "100%" }} disabled={disabledItems?.includes('reqDeliDate')} />,
    },
    {
      label: t("sale.smtStartDate"),
      name: "smtStartDate",
      getValueProps: (value) => ({ value: value && dayjs(Number(value)) }),
      normalize: (value) => value && dayjs(value).valueOf(),
      children: <DatePicker style={{ width: "100%" }} disabled={disabledItems?.includes('smtStartDate')} />,
      hidden: pageId === PageId.SALE_NOT_PASS,
    },
    {
      label: t("sale.preFinishDate"),
      name: "preFinishDate",
      getValueProps: (value) => ({ value: value && dayjs(Number(value)) }),
      normalize: (value) => value && dayjs(value).valueOf(),
      children: <DatePicker style={{ width: "100%" }} disabled={disabledItems?.includes('preFinishDate')} />,
      hidden: pageId === PageId.SALE_NOT_PASS,
    },
    {
      label: t("sale.urgencyLevel"),
      name: "urgencyLevel",
      children: getInput({
        maxLength: 50,
        placeholder: t("sale.plUrgencyLevel"),
      }),
    },
    {
      label: t("sale.specialReq"),
      name: "specialReq",
      children: (
        <Input.TextArea
          maxLength={3000}
          placeholder={t("sale.plSpecialReq")}
          autoSize={{ maxRows: 8, minRows: 2 }}
          showCount
        />
      ),
    },
    {
      label: t("sale.reRemark"),
      name: "remark",
      children: (
        <Input.TextArea
          maxLength={3000}
          placeholder={t("sale.plReRemark")}
          autoSize={{ maxRows: 8, minRows: 2 }}
          showCount
        />
      ),
    },
  ]

  const infoReadOnlyForm: FormItemProps<SaleOrderType>[] = [
    {
      noStyle: true,
      children: <Title level={4}>{t("sale.customerInfo")}</Title>
    },
    {
      noStyle: true,
      children: detail && <Descriptions items={getCustomer(detail)}></Descriptions>
    },
  ]

  const getFormatData: (data: SaleItemState | null) => SaleOrderType | null = data => {
    const extTypes = ['+', '-'];
    if (data) {
      const { items, id, logBookId, creator, updater, extPrice, requirement, docId, commissionRate, currency } = data.order;
      const isOk = extPrice && extTypes.includes(extPrice.slice(0, 1));
      let amount = Number(data.amount || 0);
      let bAmount = Number(data.bamount || 0);
      setMaxDisc(amount)
      setMaxBDisc(bAmount)
      return {
        docId,
        commissionRate,
        currency,
        itemPriceHis: {
          id: data.priceHisId,
          priceId: data.priceId ?? '',
          customerId: data.order?.customerId,
          productId: data.productId,
          price: data.price,
          desc: "",
          product: data.product ?? null,
          customer: data.customer ?? null,
          estimateDays: null,
          expiredAt: null,
          mfgr: '',
          customerModel: ''
        },
        customer: data.order.customer ?? null,
        remark: data.remark,
        mfgr: data?.order?.mfgr ?? '',
        customerModel: data?.order?.customerModel ?? '',
        reqDeliDate: data?.order?.reqDeliDate ?? null,
        smtStartDate: data?.order?.smtStartDate ?? null,
        preFinishDate: data?.order?.preFinishDate,
        urgencyLevel: data?.order?.urgencyLevel ?? '',
        specialReq: data?.order?.specialReq ?? '',
        shippingMethod: data.order?.shippingMethod ?? '',
        extPrice: {
          type: isOk ? extPrice?.slice(0, 1) as ExtPriceType : '-',
          num: isOk ? parseFloat(extPrice.slice(1, extPrice.length)) : 0,
        },
        product: data.product,
        price: data.price,
        type: data.order?.type,
        customerId: data.order?.customerId,
        receiver: data.order?.receiver,
        receiverContact: data.order?.receiverContact,
        receiverMobile: data.order?.receiverMobile,
        receiverAddress: data.order?.receiverAddress,
        totalAmount: data.order?.totalAmount,
        innerNumber: data.order?.innerNumber,
        country: data.order?.country,
        taxRate: data.order?.taxRate,

        quantity: data.quantity, // 数量
        discountedPrice: data.discountedPrice || '',//调整价格
        amount: data.amount, // 总价是真实价格四舍五入之后的数据
        discount: data.discount || '',//优惠折扣
        realAmount: data.realAmount || '',//销售总价

        bquantity: data.bquantity, // 数量
        bdiscountedPrice: data.bdiscountedPrice || '',//调整价格
        bamount: data.bamount, // 总价是真实价格四舍五入之后的数据
        bdiscount: data.bdiscount || '',//优惠折扣
        brealAmount: data.brealAmount,//销售总价

        totalRealAmount: data.totalRealAmount,//销售总价
        receiverFax: data.order.receiverFax ?? '',
        receiverPhone: data.order.receiverPhone ?? '',
      }
    } else {
      return null
    }
  }

  const formItems = [...saleInputForm];
  data ? formItems.push(...infoReadOnlyForm, ...productInputForm) : formItems.push(...productInputForm)



  const onClear = () => {
    setCustomerId('')
    setProduct(null)
    setDetail(null)
    setPastedCustomer(null)
    setDiscountedPriceSign("-")
    setBdiscountedPriceSign("-")
    formRef?.current?.resetFields();
    props.handleCancel();
  }

  const printChildren = (
    <OrderPrintWrap style={{ background: '#f00' }}>
      <div className="order-detail--print">
        <div className="order-detail--print--title">吉翁科技(深圳)有限公司</div>
        <div className="order-detail--print--subtitle">销售订单</div>
        <table className="order-detail--print--order">
          <tr>
            <td>
              业务类型: <SelectOrderType readOnly value={detail?.order?.type} />
            </td>
            <td>订单日期: {detail?.preFinishDate ? dateFormat(dayjs(detail?.preFinishDate)) : ''}</td>
            <td>
              订单号: {detail?.order?.number}
            </td>
          </tr>
          <tr>
            <td></td>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <td></td>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
        </table>
      </div>
    </OrderPrintWrap>
  )

  return (
    <React.Fragment>
      <SModalForm<SaleOrderType>
        width={850}
        modelFormRef={formRef}
        formItems={formItems}
        data={detail}
        title={
          <Flex justify="space-between" align="center">
            {props.leftTitle}
            <Flex align='center' gap={16} className="right-action--wrap">
              {false && <Print contentRef={printRef} documentTitle={`销售订单 ${detail?.order?.number}`} />}
              {!data && (<Tooltip title={t("common.paste")}>
                <Button onClick={() => {
                  onPaste().then(res => {
                    const pastedData: SaleItemState = JSON.parse(res);
                    const data: SaleOrderType | null = getFormatData(pastedData);
                    setDetail(data)
                    setCustomerId(data?.customerId ?? '')
                    setProductKey(productKey + 1)
                    setProduct(data?.product ?? null)
                    setPrice(data?.itemPriceHis?.price || '')
                    setDiscountedPriceSign(getAdjustmentSignByAmount(pastedData?.amount, pastedData?.quantity, pastedData?.price, pastedData?.discountedPrice))
                    setBdiscountedPriceSign(getAdjustmentSignByAmount(pastedData?.bamount, pastedData?.bquantity, pastedData?.price, pastedData?.bdiscountedPrice))
                    // 设置粘贴的customer数据
                    setPastedCustomer(data?.customer ?? null)

                    if (data) {
                      formRef?.current?.setFieldsValue(data)
                    }
                  }).catch(() => {
                    notification.error({ placement: 'top', message: t("common.pasteError") })
                  })
                }} icon={<ReadOutlined />} type="text" />
              </Tooltip>)}
              <Button onClick={onClear} icon={<CloseOutlined />} type="text" />
            </Flex>
          </Flex>
        }
        handleOk={(res) => {
          const item: SaleOrderAddItem = {
            priceHisId: res.itemPriceHis?.id ?? '',
            price: res.itemPriceHis?.price ?? '',
            remark: res.remark,
            productName: '',
            quantity: res.quantity || 0, // 数量
            discountedPrice: res.discountedPrice ?? '0',//调整价格
            amount: res.amount ?? '0',
            discount: res.discount ?? '0',//优惠折扣
            realAmount: res.realAmount ?? '0',//销售总价

            bquantity: res.bquantity || 0, // 数量
            bdiscountedPrice: res.bdiscountedPrice ?? '0',//调整价格
            bdiscount: res.bdiscount ?? '0',//优惠折扣
            bamount: res.bamount ?? '0',
            brealAmount: res.brealAmount ?? '0',//销售总价
            totalRealAmount: res.totalRealAmount ?? '0',//销售总价
          }

          let amount = divided(res.totalRealAmount ?? '0', plus('1', divided(res.taxRate, '100')))

          const detail: SaleOrderAdd = {
            docId: res?.docId || '',
            type: res.type,
            customerId: res?.customerId || '',
            country: res?.country || '',
            taxRate: res?.taxRate || '',
            receiver: res?.receiver || '',
            receiverContact: res?.receiverContact || '',
            receiverMobile: res?.receiverMobile || '',
            receiverAddress: res?.receiverAddress || '',
            preApproval: 0,
            innerNumber: res?.innerNumber || '',
            shippingMethod: res?.shippingMethod ?? '',
            finishState: 0,
            extPrice: res.extPrice?.num && res.extPrice?.num !== 0 ? `${res.extPrice?.type}${res.extPrice?.num}` : '-0',
            items: [item],
            currency: res?.currency,
            commissionRate: res?.commissionRate ? '' + res.commissionRate : '',
            productId: product?.id || null,
            preFinishDate: res.preFinishDate,
            reqDeliDate: res.reqDeliDate,
            smtStartDate: res.smtStartDate,

            totalAmount: dp(amount),
            realAmount: amount,

            totalRealAmount: res.totalRealAmount ?? '0',

            mfgr: res?.mfgr || '',
            customerModel: res?.customerModel || '',
            urgencyLevel: res?.urgencyLevel || '',
            specialReq: res?.specialReq || '',
          };
          return handleOk(detail, item, data?.id ?? '')
        }}
        {...props}
        destroyOnClose
        onCancel={onClear}
        handleCancel={onClear}
        formProps={{
          disabled: !!props.readOnly
        }}
        okButtonProps={{
          disabled: !!props.readOnly
        }}
      />
      {detail && false && printChildren}
      <Modal
        open={showList}
        onCancel={() => setShowList(false)}
        footer={null}
        title={t('purchase.outOrder')}
      >
        <div style={{ maxHeight: 400, overflow: 'auto' }}>
          {/* <h3>{t('purchase.已生成出库单')}：</h3> */}
          {data?.inventoryOrders && data?.inventoryOrders.map((item: any) => {
            return (
              <div style={{ border: "1px solid #c6bfbf", borderRadius: 10, padding: '15px 15px', marginBottom: 10 }}>
                <span>{t('purchase.outOrderNumber')}:{item.number}</span>
                <span style={{ marginLeft: 50 }}>{t('purchase.state')}:{item.confirmed === 1 ? (<Tag color={'blue'}>{t('purchase.isOut')}</Tag>) : (<Tag color={'red'}>{t('purchase.notOut')}</Tag>)}</span>
                <Divider style={{ margin: '5px 0' }} />
                <Form labelCol={{ span: 4 }}>
                  {item.items.map((d: any) => {
                    return (
                      <div>
                        <Form.Item label={t('purchase.goodName')} name={'typeName'} initialValue={d?.product?.name}>
                          <Input disabled />
                        </Form.Item>
                        <Form.Item label={t('purchase.goodCode')} name={'typeCode'} initialValue={d.product.code}>
                          <Input disabled />
                        </Form.Item>
                        <Form.Item label={t('purchase.goodModel')} name={'type'} initialValue={d.product.model}>
                          <Input disabled />
                        </Form.Item>
                        <Form.Item label={t('purchase.quantity')} name={'num'} initialValue={d.quantity}>
                          <Input disabled />
                        </Form.Item>
                      </div>
                    )
                  })}

                </Form>
              </div>
            )
          })}
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default OrderForm;
